import React from 'react';
import PropTypes from 'prop-types';

const FlashLoadingOverlay = ({ isFlashing, flashProgress }) => {
  if (!isFlashing) return null;

  const overlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    padding: '16px'
  };

  const containerStyle = {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    width: '100%',
    maxWidth: '280px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e5e7eb'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '12px'
  };

  const iconWrapperStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    backgroundColor: '#dbeafe',
    borderRadius: '50%',
    marginBottom: '8px'
  };

  const titleStyle = {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: '4px',
    margin: 0
  };

  const subtitleStyle = {
    fontSize: '12px',
    color: '#6b7280',
    margin: 0
  };

  const progressContainerStyle = {
    marginBottom: '12px'
  };

  const progressHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px'
  };

  const progressLabelStyle = {
    fontSize: '12px',
    fontWeight: '500',
    color: '#4b5563'
  };

  const progressValueStyle = {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#2563eb'
  };

  const progressBarBgStyle = {
    width: '100%',
    backgroundColor: '#e5e7eb',
    borderRadius: '9999px',
    height: '8px',
    overflow: 'hidden'
  };

  const progressBarFillStyle = {
    background: 'linear-gradient(to right, #3b82f6, #2563eb)',
    height: '100%',
    borderRadius: '9999px',
    transition: 'width 0.3s ease-out',
    width: `${flashProgress}%`,
    position: 'relative',
    overflow: 'hidden'
  };

  const stagesStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  };

  const warningStyle = {
    marginTop: '12px',
    padding: '8px',
    backgroundColor: '#fffbeb',
    border: '1px solid #fcd34d',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px'
  };

  const warningTextStyle = {
    fontSize: '11px',
    color: '#92400e',
    lineHeight: '1.4',
    margin: 0
  };

  return (
    <div style={overlayStyle}>
      <div style={containerStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={iconWrapperStyle}>
            <Spinner />
          </div>
          <h3 style={titleStyle}>Đang Flash</h3>
          <p style={subtitleStyle}>Không ngắt kết nối...</p>
        </div>

        {/* Progress Bar */}
        <div style={progressContainerStyle}>
          <div style={progressHeaderStyle}>
            <span style={progressLabelStyle}>Tiến độ</span>
            <span style={progressValueStyle}>{flashProgress}%</span>
          </div>
          <div style={progressBarBgStyle}>
            <div style={progressBarFillStyle}>
              <Shimmer />
            </div>
          </div>
        </div>

        {/* Stage Indicators */}
        <div style={stagesStyle}>
          <FlashStage 
            label="Bootloader" 
            active={flashProgress >= 0 && flashProgress < 40}
            completed={flashProgress >= 40}
          />
          <FlashStage 
            label="Partitions" 
            active={flashProgress >= 40 && flashProgress < 70}
            completed={flashProgress >= 70}
          />
          <FlashStage 
            label="Firmware" 
            active={flashProgress >= 70 && flashProgress < 95}
            completed={flashProgress >= 95}
          />
          <FlashStage 
            label="Rebooting..." 
            active={flashProgress >= 95}
            completed={flashProgress === 100}
          />
        </div>

        {/* Warning */}
        <div style={warningStyle}>
          <svg style={{width: '14px', height: '14px', color: '#d97706', flexShrink: 0, marginTop: '2px'}} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <p style={warningTextStyle}>Thiết bị sẽ tự khởi động lại</p>
        </div>
      </div>
    </div>
  );
};

const Spinner = () => (
  <svg style={{width: '20px', height: '20px', color: '#2563eb', animation: 'spin 1s linear infinite'}} fill="none" viewBox="0 0 24 24">
    <circle style={{opacity: 0.25}} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path style={{opacity: 0.75}} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

const Shimmer = () => (
  <div style={{
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.3), transparent)',
    animation: 'shimmer 2s infinite'
  }} />
);

const FlashStage = ({ label, active, completed }) => {
  const stageStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px',
    borderRadius: '4px',
    transition: 'all 0.3s'
  };

  const iconStyle = {
    flexShrink: 0,
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s',
    backgroundColor: completed ? '#22c55e' : active ? '#3b82f6' : '#d1d5db',
    animation: active ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none'
  };

  const labelStyle = {
    fontSize: '12px',
    fontWeight: '500',
    transition: 'color 0.3s',
    color: completed ? '#16a34a' : active ? '#2563eb' : '#6b7280'
  };

  return (
    <div style={stageStyle}>
      <div style={iconStyle}>
        {completed ? (
          <svg style={{width: '12px', height: '12px', color: 'white'}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        ) : active ? (
          <svg style={{width: '10px', height: '10px', color: 'white', animation: 'spin 1s linear infinite'}} fill="none" viewBox="0 0 24 24">
            <circle style={{opacity: 0.25}} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path style={{opacity: 0.75}} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          <div style={{width: '6px', height: '6px', backgroundColor: '#6b7280', borderRadius: '50%'}} />
        )}
      </div>
      <span style={labelStyle}>{label}</span>
    </div>
  );
};

FlashLoadingOverlay.propTypes = {
  isFlashing: PropTypes.bool.isRequired,
  flashProgress: PropTypes.number.isRequired
};

FlashStage.propTypes = {
  label: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  completed: PropTypes.bool.isRequired
};

// Add keyframes animations
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;
document.head.appendChild(styleSheet);

export default FlashLoadingOverlay;